import { env } from '@/env';
import { isLocalRun } from '@/lib/local-run';

const OPT_OUT_KEY = 'umami.disabled';

export type AnalyticsEvent =
  | 'order_action'
  | 'item_stock_toggled'
  | 'menu_edited'
  | 'setup_changed'
  | 'reservation_action'
  | 'delivery_timing_changed'
  | 'notification_health';

export type EventData = Record<string, string | number | boolean | undefined>;

interface UmamiTracker {
  track: (payload: (existing: TrackerPayload) => TrackerPayload) => void;
  identify: (data: EventData) => void;
}

interface TrackerPayload {
  url?: string;
  name?: string;
  data?: EventData;
}

declare global {
  interface Window {
    umami?: UmamiTracker;
  }
}

type QueuedCall = (tracker: UmamiTracker) => void;

let queued: QueuedCall[] | null = null;

function withTracker(call: QueuedCall): void {
  if (window.umami) {
    call(window.umami);
    return;
  }
  queued?.push(call);
}

function flushQueue(): void {
  const pending = queued ?? [];
  queued = null;
  if (!window.umami) return;
  for (const call of pending) call(window.umami);
}

function loadScript(file: string, attributes: Record<string, string>, onLoad?: () => void): void {
  const script = document.createElement('script');
  script.src = `${env.umamiHost.replace(/\/$/, '')}/${file}`;
  script.async = true;
  for (const [key, value] of Object.entries(attributes)) {
    script.setAttribute(key, value);
  }
  if (onLoad) {
    script.addEventListener('load', onLoad);
    script.addEventListener('error', onLoad);
  }
  document.head.appendChild(script);
}

export function initUmami(): void {
  if (typeof window === 'undefined' || window.umami) return;
  if (isLocalRun()) return;
  if (!env.umamiWebsiteId || !env.umamiHost) return;

  const identity = {
    'data-website-id': env.umamiWebsiteId,
    'data-host-url': env.umamiHost,
  };

  queued = [];
  loadScript(
    'script.js',
    {
      ...identity,
      'data-auto-track': 'true',
      'data-auto-pageview': 'true',
      'data-do-not-track': 'true',
      'data-performance': 'true',
      'data-tag': env.deployEnv,
    },
    flushQueue,
  );

  // Replay masking is a SERVER setting, not an attribute here; the default
  // masks inputs but not text. Requires maskLevel 'strict' in Umami.
  loadScript('recorder.js', identity);
}

export function identifyUmami(u: {
  userId: string;
  restaurantId: string;
  restaurantRole?: string | null;
}): void {
  withTracker((tracker) =>
    tracker.identify({
      id: u.userId,
      restaurantId: u.restaurantId,
      restaurantRole: u.restaurantRole ?? 'franchise-wide',
    }),
  );
}

export function trackEvent(name: AnalyticsEvent, data?: EventData): void {
  withTracker((tracker) => tracker.track((payload) => ({ ...payload, name, data })));
}

export function isAnalyticsOptedOut(): boolean {
  return localStorage.getItem(OPT_OUT_KEY) !== null;
}

export function setAnalyticsOptOut(optedOut: boolean): void {
  if (optedOut) {
    localStorage.setItem(OPT_OUT_KEY, 'true');
  } else {
    localStorage.removeItem(OPT_OUT_KEY);
  }
}
